import React, { useEffect, useState, useCallback } from 'react'
import { makeStyles, Avatar, ConfirmationDialog, IconButton, PopupMenu, PopupMenuItem } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useDialogs, useUser } from '@paintscout/ui-business'
import { getAvatarThumbUrl } from '@paintscout/util'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import RefreshIcon from '@material-ui/icons/Refresh'
import SettingsIcon from '@material-ui/icons/Settings'
import UpdateIcon from '@material-ui/icons/SystemUpdate'
import { Link } from 'react-router-dom'
import Role from '../Role'

const useStyles = makeStyles<Theme, ProfileButtonProps>(
  (theme) => ({
    root: {
      color: theme.palette.common.black
    },
    iconButton: {
      color: 'inherit',
      padding: theme.spacing(1)
    },
    icon: {
      fontSize: '1.5em',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.25em'
      }
    },
    updateMenuItem: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightMedium
    },
    anchor: {
      textDecoration: 'none',
      '&:focus': {
        outline: 'none'
      },
      color: theme.palette.common.black
    },
    headerProfileImage: {
      width: '1.4em',
      height: '1.4em',
      borderRadius: '50%',
      objectFit: 'cover'
    },
    profileImage: {
      '& img': {
        borderRadius: '50%',
        objectFit: 'cover'
      }
    },
    newIcon: {
      marginLeft: theme.spacing(1)
    },
    profileAvatar: {
      marginRight: theme.spacing(2)
    }
  }),
  { name: 'ProfileButton' }
)

export interface ProfileButtonProps {
  classes?: StyleClasses<typeof useStyles>
}

/**
 * Rendered sizes of the two headshots, in CSS pixels. Both are set in `em`, so neither size is
 * readable from a prop — `getAvatarThumbUrl` has to be told.
 *
 * - Header: `headerProfileImage` is `1.4em` and MUI's `IconButton` root pins font-size to 24px, so
 *   1.4em is 33.6px regardless of the user's `fontScale`.
 * - Menu: `MenuItem` sizes its `img` at `1em` of `1.5em`, giving 24px at `fontScale` 1 (21.6px at the
 *   default 0.9).
 *
 * Keep these close to the real sizes. Over-requesting puts the browser back to downscaling a bitmap
 * that is much larger than the box, which is the blur this fixes (#22459).
 */
const HEADER_AVATAR_SIZE = 34
const MENU_AVATAR_SIZE = 24

function ProfileButton(props: ProfileButtonProps) {
  const classes = useStyles(props)
  const { openDialog, dismissDialog } = useDialogs()
  const { user, isTrial, trialExpired, isChurned } = useUser()
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)

  const onUpdateReady = useCallback((_: any) => {
    setIsUpdateAvailable(true)
  }, [])

  useEffect(() => {
    window.addEventListener('UPDATE_READY', onUpdateReady)
    return () => {
      window.removeEventListener('UPDATE_READY', onUpdateReady)
    }
  }, [onUpdateReady])

  const restart = useCallback(() => {
    // Clear any unsaved-changes guard (RoutePrompt sets `onbeforeunload = () => true`) before
    // reloading — on WebKit a lingering guard surfaces a native "Leave site?" prompt that can
    // freeze the page.
    const reload = () => {
      window.onbeforeunload = () => null
      window.location.reload()
    }

    // No global unsaved-changes context exists; probe the handler RoutePrompt installs.
    const hasUnsavedChanges =
      typeof window.onbeforeunload === 'function' && window.onbeforeunload(new Event('beforeunload') as any) === true

    if (hasUnsavedChanges) {
      openDialog(ConfirmationDialog, {
        title: 'Restart',
        message: (
          <div>
            The page will be reloaded. Any unsaved data will be lost!
            <br />
            <br />
            Is this OK?
          </div>
        ),
        onConfirm: reload,
        onCancel: dismissDialog
      })
    } else {
      reload()
    }
  }, [openDialog, dismissDialog])

  const renderPopupComponent = useCallback(() => {
    const userProfilePictureUrl = user?.user_metadata?.customAvatar?.value
    const userName = user?.name || user?.email
    const userColor = user?.user_metadata?.color

    const Icon = userProfilePictureUrl ? (
      <img
        src={getAvatarThumbUrl(userProfilePictureUrl, HEADER_AVATAR_SIZE)}
        // declare the intrinsic size so the browser doesn't have to infer it from the bitmap. This is
        // all we can do for S3-hosted avatars, which have no URL-based resize.
        width={HEADER_AVATAR_SIZE}
        height={HEADER_AVATAR_SIZE}
        alt={userName}
        className={classes.headerProfileImage}
      />
    ) : (
      <Avatar
        backgroundColor="#FFFFFF"
        disableTooltip
        filled
        avatarColor={userColor}
        width="30px"
        height="30px"
        fontSize="13px"
      >
        {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
      </Avatar>
    )

    return (
      <IconButton classes={{ root: classes.iconButton }} data-testid="profileButton">
        {Icon}
      </IconButton>
    )
  }, [user, classes])

  const userProfilePictureUrl = user?.user_metadata?.customAvatar?.value
  const userName = user?.name || user?.email
  const userColor = user?.user_metadata?.color

  const restartOnlyMenu = [
    <PopupMenuItem key="restart" icon={RefreshIcon} onClick={restart}>
      Restart
    </PopupMenuItem>,
    <Link key="logout" to="/logout" className={classes.anchor}>
      <PopupMenuItem icon={ArrowBackIcon}>Log Out</PopupMenuItem>
    </Link>
  ]

  const fullMenu = [
    isUpdateAvailable && (
      <PopupMenuItem
        key="update"
        classes={{ root: classes.updateMenuItem }}
        icon={UpdateIcon}
        onClick={() => {
          openDialog(ConfirmationDialog, {
            title: 'Update',
            message: (
              <div>
                The page will be reloaded to finish the update. Any unsaved data will be lost!
                <br />
                <br />
                Is this OK?
              </div>
            ),
            onConfirm: () => {
              openDialog(
                ConfirmationDialog,
                {
                  title: 'Update',
                  message: <strong>Please make sure any other PaintScout tabs are closed, and then click OK</strong>,
                  onConfirm: () => window.location.reload(),
                  onCancel: dismissDialog
                },
                {
                  replace: true
                }
              )
            },
            onCancel: dismissDialog
          })
        }}
      >
        Update Available
      </PopupMenuItem>
    ),
    <Link key="profile" to="/profile" className={classes.anchor}>
      {userProfilePictureUrl ? (
        <PopupMenuItem
          classes={{ root: classes.profileImage }}
          image={getAvatarThumbUrl(userProfilePictureUrl, MENU_AVATAR_SIZE)}
        >
          Your Profile
        </PopupMenuItem>
      ) : (
        <PopupMenuItem
          icon={() => (
            <Avatar
              backgroundColor="#FFFFFF"
              disableTooltip
              filled
              className={classes.profileAvatar}
              avatarColor={userColor}
              width="24px"
              height="24px"
              fontSize="10px"
              style={{
                paddingLeft: 0,
                paddingTop: 0
              }}
            >
              {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
            </Avatar>
          )}
        >
          Your Profile
        </PopupMenuItem>
      )}
    </Link>,
    <Link key="settings" to="/settings" className={classes.anchor}>
      <Role permission={'write:account-settings'}>
        <PopupMenuItem icon={SettingsIcon}>Account Settings</PopupMenuItem>
      </Role>
    </Link>,
    <PopupMenuItem key="restart" icon={RefreshIcon} onClick={restart}>
      Restart
    </PopupMenuItem>,
    <Link key="logout" to="/logout" className={classes.anchor}>
      <PopupMenuItem icon={ArrowBackIcon}>Log Out</PopupMenuItem>
    </Link>
  ]

  const restrictProfileMenu = (isTrial && trialExpired) || isChurned

  return (
    <>
      <PopupMenu classes={{ root: classes.root }} component={renderPopupComponent()} id="profile">
        {restrictProfileMenu ? restartOnlyMenu : fullMenu}
      </PopupMenu>
    </>
  )
}

export default ProfileButton
